import {
  Alert,
  Button,
  DatePicker,
  Divider,
  Popconfirm,
  Space,
  Table,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { DATE_FORMATS } from '../../../../../../common/constants';
import ModalComponent from '../../../../../../components/ModalComponent';
import {
  DELIVERY_DATE_WEEK,
  ORDER_DELIVERY_DATE_FORMAT,
} from '../../../../ultraPlanConstants';
import {
  getUnorderedWeekSlots,
  getWeekSlots,
} from '../../../../ultraPlanUtils';
import OrderResultsTable from './OrderResultsTable';
import { getToyLabel } from './RoadmapToyPicker';
import ToyThumbnail from './ToyThumbnail';

const { Text } = Typography;

const isPastDate = (current) => current && current < dayjs()?.startOf('day');

const getOrderRows = (week) =>
  getUnorderedWeekSlots(week)?.map(({ position, slotConfig, slot }) => ({
    key: position,
    slotLabel: slotConfig?.label,
    toy: slot?.toy,
  }));

const columns = [
  {
    title: 'Toy',
    key: 'toy',
    render: (_, row) => (
      <div className="roadmap-toy-option">
        <ToyThumbnail toy={row?.toy} size={40} preview={false} />
        <div className="roadmap-toy-option-info">
          <Text type="secondary" className="ultra-plan-caption">
            {row?.slotLabel}
          </Text>
          <div>{getToyLabel(row?.toy)}</div>
        </div>
      </div>
    ),
  },
];

const PlaceWeekOrderModal = ({
  week,
  userNumber,
  loading,
  onPlaceOrder,
  onCancel,
}) => {
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [results, setResults] = useState(null);
  const canSetDeliveryDate = week?.weekNumber === DELIVERY_DATE_WEEK;
  const rows = useMemo(() => getOrderRows(week), [week]);
  const alreadyOrderedCount = getWeekSlots(week)?.length - rows?.length;

  useEffect(() => {
    setDeliveryDate(null);
    setResults(null);
  }, [week]);

  const handleConfirm = async () => {
    const formattedDate =
      canSetDeliveryDate && deliveryDate
        ? deliveryDate?.format(ORDER_DELIVERY_DATE_FORMAT)
        : null;
    const items = await onPlaceOrder({
      weekNumber: week?.weekNumber,
      toys: rows?.map((row) => ({
        toyNumber: row?.toy?.toyNumber,
        ...(formattedDate && { expectedDeliveryDate: formattedDate }),
      })),
    });
    if (items) setResults(items);
  };

  const renderFooter = () => {
    if (results) {
      return (
        <Button type="primary" onClick={onCancel}>
          Done
        </Button>
      );
    }
    return (
      <Space>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Popconfirm
          title={`Place order for Week ${week?.weekNumber}?`}
          description={`${rows?.length} toy${
            rows?.length === 1 ? '' : 's'
          } will be ordered for subscriber #${userNumber}. Ordered toys can no longer be changed.`}
          okText="Place Order"
          cancelText="Back"
          onConfirm={() => {
            handleConfirm();
          }}
          disabled={loading}
        >
          <Button type="primary" loading={loading}>
            Place Order
          </Button>
        </Popconfirm>
      </Space>
    );
  };

  return (
    <ModalComponent
      open={!!week}
      width={720}
      title={`${results ? 'Order Result' : 'Place Order'} · Week ${
        week?.weekNumber || ''
      }`}
      onCancel={loading ? undefined : onCancel}
      closable={!loading}
      footer={renderFooter()}
      destroyOnClose
    >
      <Divider />
      {results ? (
        <OrderResultsTable items={results} />
      ) : (
        <>
          <Alert
            className="mb-16"
            type="warning"
            showIcon
            message="This places real orders for the subscriber."
            description={
              canSetDeliveryDate
                ? 'The toys below are ordered together. The delivery date is optional; leave it empty and the system schedules it. Nothing is ordered until you confirm.'
                : 'The toys below are ordered together. Delivery is scheduled automatically. Nothing is ordered until you confirm.'
            }
          />
          {alreadyOrderedCount > 0 && (
            <Alert
              className="mb-16"
              type="info"
              showIcon
              message={`${alreadyOrderedCount} toy${
                alreadyOrderedCount === 1 ? ' is' : 's are'
              } already ordered for this week and won't be ordered again.`}
            />
          )}
          {canSetDeliveryDate && (
            <Space className="mb-16" wrap>
              <Text>{`Expected delivery for Week ${week?.weekNumber}`}</Text>
              <DatePicker
                format={DATE_FORMATS?.REGULAR}
                placeholder="Auto (optional)"
                value={deliveryDate}
                disabledDate={isPastDate}
                disabled={loading}
                onChange={setDeliveryDate}
              />
            </Space>
          )}
          <Table
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={false}
          />
        </>
      )}
    </ModalComponent>
  );
};

export default PlaceWeekOrderModal;
